import type { ReactNode } from 'react';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    TASK_ERROR_MISSING,
    TASK_ERROR_MISSING_DETAIL,
    TASK_ERROR_PROCESSING,
} from '../../strings';
import type { TaskStatus } from './TaskStatus';
import { isTaskStatus, validateTaskStatus } from './TaskStatus';

export interface Task {
    name: string;
    func: (taskStatus: TaskStatus) => Promise<TaskStatus>;
}

type TaskUpdateAction = TaskStatus | ((prev: TaskStatus) => TaskStatus);
type TaskAddOrUpdateAction = TaskStatus | ((prev?: TaskStatus) => TaskStatus);

export interface TaskProcessorContextType {
    taskStatuses: TaskStatus[];
    addTaskStatus: (task: Omit<TaskStatus, 'started' | 'updated'>) => void;
    removeTaskStatus: (taskID: string) => void;
    updateTaskStatus: (taskID: string, task: TaskUpdateAction) => void;
    addOrUpdateTaskStatus: (
        taskID: string,
        task: TaskAddOrUpdateAction,
        options?: { onCancel?: () => void },
    ) => void;
    dismissTaskStatus: (taskID: string) => void;
    registerTasks: (tasks: Task[]) => void;
    registerTaskActionHandler: (
        taskType: string,
        handler: (taskStatus: TaskStatus) => void,
    ) => () => void;
    triggerTaskAction: (taskStatus: TaskStatus) => void;
    openTaskViewer: () => void;
    registerOpenTaskViewer: (fn: () => void) => () => void;
    getCancelHandler: (taskID: string) => (() => void) | undefined;
}

/**
 * The time we wait between updating the tasks, in seconds
 */
const POLL_INTERVAL = 1;

// Create context with default value
const TaskProcessorContext = createContext<TaskProcessorContextType | undefined>(undefined);

// FUTURE: Should we associate taskStatuses with getEvoInstance() details to filter localStorage tasks in viewer?
// Create provider component

/**
 * See libraries/ui/content-area/src/TaskViewer/README.md for documentation on how to use this component and its associated context.
 * @param children The child components that will have access to the TaskProcessor context.
 * @returns JSX.Element
 */
export function TaskProcessorProvider({ children }: { children: ReactNode }) {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [currentlyPolling, setCurrentlyPolling] = useState(false);
    const [taskStatuses, setTaskStatuses] = useState<TaskStatus[] | null>(null); // null until we load from localstorage

    async function pollJobs() {
        const activeTasks = (taskStatuses ?? []).filter((task) => task.active);
        const taskFuncs: Promise<TaskStatus<any>>[] = [];
        const processedTaskStatuses: TaskStatus[] = [];

        if (activeTasks.length !== 0) {
            setCurrentlyPolling(true);
            for (const taskStatus of activeTasks) {
                const func = tasks.find((task) => task.name === taskStatus.taskType)?.func;

                // if we cant find the func, update state w error
                if (!func) {
                    console.error(
                        `[TaskProcessor] - pollJobs - task with name ${taskStatus.taskType} not found`,
                    );
                    const newStatus = taskStatus;
                    newStatus.error = {
                        title: TASK_ERROR_MISSING,
                        detail: TASK_ERROR_MISSING_DETAIL(taskStatus.taskType),
                    };
                    newStatus.active = false;
                    processedTaskStatuses.push(newStatus);
                } else {
                    taskFuncs.push(
                        func(taskStatus).catch((err) => {
                            console.error(
                                `[TaskProcessor] - pollJobs - error processing task with name ${taskStatus.taskType}:`,
                            );
                            const newStatus = taskStatus;
                            newStatus.error = {
                                title: TASK_ERROR_PROCESSING,
                                detail: (err as Error).message,
                            };
                            newStatus.active = false;
                            return newStatus;
                        }),
                    );
                }
            }

            processedTaskStatuses.push(...(await Promise.all(taskFuncs)));

            const processedIDs = processedTaskStatuses.map((taskStatus) => taskStatus.id);
            setTaskStatuses((prev) => [
                ...(prev ?? []).filter((taskStatus) => !processedIDs.includes(taskStatus.id)),
                ...processedTaskStatuses,
            ]);
            // whenever tasks change, save to localStorage
            setLocalStorage(processedTaskStatuses);
            // notify other tabs of the new task statuses
            postMessage(processedTaskStatuses);
            setCurrentlyPolling(false);
        }
    }

    // useEffects ==================================================================

    // whenever task statuses change, update local storage
    useEffect(() => {
        if (taskStatuses !== null) {
            setLocalStorage(taskStatuses);
        }
    }, [taskStatuses]);

    // load tasks from local storage
    useEffect(() => {
        // only update tasks if we have new ones in local storage
        const localTaskStatuses = getLocalStorage();
        if (localTaskStatuses.length !== 0) {
            setTaskStatuses(localTaskStatuses);
        }
    }, []);

    // poll the jobs
    useEffect(() => {
        const interval = setInterval(() => {
            if (!currentlyPolling) {
                pollJobs();
            }
        }, 1000 * POLL_INTERVAL);

        return () => {
            clearInterval(interval);
        };
    }, [taskStatuses, currentlyPolling]);

    // Helper Functions =============================================================

    const getLocalStorage = useCallback(() => {
        const localTasks = localStorage.getItem('taskProcessorTasks');
        if (localTasks) {
            const localTaskStatuses: TaskStatus[] = JSON.parse(localTasks);

            // filter out any which do not match the current taskStatuse interface
            const validTaskStatuses = localTaskStatuses.filter((task) => validateTaskStatus(task));

            return validTaskStatuses;
        }
        return [];
    }, []);

    const setLocalStorage = useCallback((newTasks: TaskStatus[]) => {
        // filter out active tasks
        const inactiveTasks = newTasks.filter((task) => !task.active);

        localStorage.setItem('taskProcessorTasks', JSON.stringify(inactiveTasks));
    }, []);

    // basic functions ==============================================================

    const registerTasks = useCallback((newTasks: Task[]) => {
        setTasks((prev) => {
            const existingTasksRemoved = prev.filter(
                (task) => !newTasks.some((newTask) => newTask.name === task.name),
            );

            return [...existingTasksRemoved, ...newTasks];
        });
    }, []);

    const addTaskStatus = useCallback(
        (task: Omit<TaskStatus, 'started' | 'updated'>) => {
            // check if we have already seen that ID
            if ((taskStatuses ?? []).every((t) => t.id !== task.id)) {
                // we add them to a buffer array so we dont get a race condition with the poller
                setTaskStatuses((prev) => [
                    ...(prev ?? []),
                    { ...task, started: Date.now(), updated: Date.now() },
                ]);
            } else {
                console.error(`[TaskProcessor] - addTask - task with id ${task.id} already exists`);
            }
        },
        [taskStatuses],
    );

    const updateTaskStatus = useCallback((taskID: string, taskStatus: TaskUpdateAction) => {
        setTaskStatuses((prev) => {
            const prevTaskStatus = (prev ?? []).find((task) => task.id === taskID);

            if (!prevTaskStatus) {
                console.error(
                    `[TaskProcessor] - updateTaskStatus - task with id ${taskID} not found`,
                    prev,
                );

                return prev;
            }

            return [
                ...(prev ?? []).filter((task) => task.id !== taskID),
                isTaskStatus(taskStatus) ? taskStatus : taskStatus(prevTaskStatus),
            ];
        });
    }, []);

    const addOrUpdateTaskStatus = useCallback(
        (
            taskID: string,
            taskStatus: TaskAddOrUpdateAction,
            options?: { onCancel?: () => void },
        ) => {
            if (options?.onCancel) {
                cancelHandlersRef.current.set(taskID, options.onCancel);
            } else {
                cancelHandlersRef.current.delete(taskID);
            }

            setTaskStatuses((prev) => {
                const prevTaskStatus = prev?.find((task) => task.id === taskID);

                if (!prevTaskStatus) {
                    // No existing task, add new task.
                    return [
                        ...(prev ?? []),
                        {
                            ...(isTaskStatus(taskStatus) ? taskStatus : taskStatus()),
                            started: Date.now(),
                            updated: Date.now(),
                        },
                    ];
                }

                // Found existing task, update.
                return [
                    ...(prev ?? []).filter((task) => task.id !== taskID),
                    isTaskStatus(taskStatus) ? taskStatus : taskStatus(prevTaskStatus),
                ];
            });
        },
        [],
    );

    const removeTaskStatus = useCallback((taskID: string) => {
        setTaskStatuses((prev) => (prev ?? []).filter((task) => task.id !== taskID));
    }, []);

    const dismissTaskStatus = useCallback(
        (taskID: string) => {
            const oldTaskStatus = (taskStatuses ?? []).find((task) => task.id === taskID);
            if (oldTaskStatus && oldTaskStatus.notification) {
                updateTaskStatus(taskID, {
                    ...oldTaskStatus,
                    notification: { ...oldTaskStatus.notification, dismissed: true },
                });
            }
        },
        [taskStatuses],
    );

    const taskActionHandlersRef = useRef<Map<string, Set<(taskStatus: TaskStatus) => void>>>(
        new Map(),
    );

    const pendingActionsRef = useRef<Map<string, TaskStatus>>(new Map());

    const cancelHandlersRef = useRef<Map<string, () => void>>(new Map());

    const getCancelHandler = useCallback((taskID: string) => {
        return cancelHandlersRef.current.get(taskID);
    }, []);

    const openTaskViewerCallbackRef = useRef<(() => void) | null>(null);

    const registerOpenTaskViewer = useCallback((fn: () => void) => {
        openTaskViewerCallbackRef.current = fn;
        return () => {
            if (openTaskViewerCallbackRef.current === fn) {
                openTaskViewerCallbackRef.current = null;
            }
        };
    }, []);

    const openTaskViewer = useCallback(() => {
        openTaskViewerCallbackRef.current?.();
    }, []);

    const registerTaskActionHandler = useCallback(
        (taskType: string, handler: (taskStatus: TaskStatus) => void) => {
            if (!taskActionHandlersRef.current.has(taskType)) {
                taskActionHandlersRef.current.set(taskType, new Set());
            }
            taskActionHandlersRef.current.get(taskType)!.add(handler);

            // Fire any pending action that was triggered before this handler registered
            const pending = pendingActionsRef.current.get(taskType);
            if (pending) {
                pendingActionsRef.current.delete(taskType);
                handler(pending);
            }

            return () => {
                taskActionHandlersRef.current.get(taskType)?.delete(handler);
            };
        },
        [],
    );

    const triggerTaskAction = useCallback((taskStatus: TaskStatus) => {
        const handlers = taskActionHandlersRef.current.get(taskStatus.taskType);
        if (handlers && handlers.size > 0) {
            handlers.forEach((handler) => handler(taskStatus));
        } else {
            // No handler registered yet — store for when it registers (e.g. after navigation)
            pendingActionsRef.current.set(taskStatus.taskType, taskStatus);
        }
    }, []);

    const value = useMemo(
        () => ({
            taskStatuses: taskStatuses ?? [],
            addTaskStatus,
            removeTaskStatus,
            updateTaskStatus,
            addOrUpdateTaskStatus,
            registerTasks,
            dismissTaskStatus,
            registerTaskActionHandler,
            triggerTaskAction,
            openTaskViewer,
            registerOpenTaskViewer,
            getCancelHandler,
        }),
        [
            taskStatuses,
            addTaskStatus,
            removeTaskStatus,
            updateTaskStatus,
            addOrUpdateTaskStatus,
            registerTasks,
            dismissTaskStatus,
            registerTaskActionHandler,
            triggerTaskAction,
            openTaskViewer,
            registerOpenTaskViewer,
            getCancelHandler,
        ],
    );

    return <TaskProcessorContext.Provider value={value}>{children}</TaskProcessorContext.Provider>;
}

// Custom hook for consuming context
export const useTaskProcessorContext = () => {
    const context = useContext(TaskProcessorContext);

    if (!context) {
        throw new Error('useTaskProcessorContext must be used within a TaskProcessorProvider');
    }
    return context;
};
